// =============================================================================
// ui/media/Lightbox.tsx — full-size viewer for a group of screenshots.
// -----------------------------------------------------------------------------
// Ported from the old client's `openImageLightbox` (index.html:11491) and its
// delegated `img.screenshot` click handler.
//
// ── WHY IT IS NOT OPTIONAL ──
//
// A thumbnail is 160×90. A chart capture at 160×90 is a smear. Beta sets
// `cursor: zoom-in` on every thumbnail for exactly that reason, and the tile
// grid without the viewer is a gallery of things you cannot read.
//
// ── 🚨 ESCAPE IS HANDLED IN THE CAPTURE PHASE, AND THAT IS THE WHOLE POINT ──
//
// The trade form is a modal. Without `capture: true` + `stopPropagation`, one
// Escape closes the lightbox AND the form underneath it — the user loses an
// unsaved trade to a keypress meant to dismiss a picture. Beta's own comment at
// :11538 says the same thing, so this is a reproduced fix, not a guess.
//
// ── ONE OBJECT URL AT A TIME ──
//
// Each navigation revokes the previous URL before creating the next, and close
// revokes the last. Blobs held by an un-revoked object URL are pinned in memory
// for the life of the document, and a chart capture is measured in megabytes.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import type { MediaBucket } from '../../io/storage/media';
import { useMediaObjectUrl } from './useMediaObjectUrl';

export interface LightboxProps {
  /** The whole sibling group, so ‹ › navigate the way beta's do. */
  files: readonly string[];
  startIndex: number;
  bucket?: MediaBucket;
  onClose: () => void;
}

export function Lightbox({ files, startIndex, bucket = 'screenshots', onClose }: LightboxProps) {
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(startIndex, files.length - 1)));

  const many = files.length > 1;
  const next = useCallback(() => { if (many) setIdx((i) => (i + 1) % files.length); }, [many, files.length]);
  const prev = useCallback(() => { if (many) setIdx((i) => (i - 1 + files.length) % files.length); }, [many, files.length]);

  // ⚠ CAPTURE PHASE. See the header — this is what stops Escape reaching the
  // modal underneath. `keydown` on document, not on the overlay, because the
  // overlay is not focused and a keypress would never reach it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, next, prev]);

  const current = files[idx] ?? '';
  const { url, error } = useMediaObjectUrl(bucket, current);

  if (!files.length) return null;

  const caption = error
    ? error
    : `${current}${many ? `   (${idx + 1} of ${files.length})` : ''}`;

  return (
    <div
      className="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Screenshot viewer"
      // Only a click on the backdrop ITSELF closes. A click that started on the
      // image and drifted must not dismiss it.
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <button className="lightbox-close" title="Close (Esc)" onClick={onClose}>×</button>
      {many ? (
        <>
          <button
            className="lightbox-nav prev" title="Previous (←)"
            onClick={(e) => { e.stopPropagation(); prev(); }}
          >‹</button>
          <button
            className="lightbox-nav next" title="Next (→)"
            onClick={(e) => { e.stopPropagation(); next(); }}
          >›</button>
        </>
      ) : null}
      {/* ⚠ `alt` is the FILENAME, not "screenshot". A screen reader announcing
          "screenshot" for all six of them describes nothing. */}
      {url ? (
        <img
          src={url} alt={current}
          onClick={(e) => { e.stopPropagation(); next(); }}
        />
      ) : null}
      <div className="lightbox-caption">{caption}</div>
    </div>
  );
}
